import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useState, useMemo, useCallback } from "react";
import { useMutation, useQuery } from "convex/react";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { useMsal } from "@azure/msal-react";
import { DataTableShell } from "../../components/table/DataTableShell";
import { EntityFormDialog } from "./components/EntityFormDialog";
import { glAccountSchema } from "./schemas/glAccountSchema";
import type { GlAccountFormData } from "./schemas/glAccountSchema";
import { Controller } from "react-hook-form";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GlAccountRow {
  _id: Id<"glAccounts">;
  number: string;
  description: string;
  accountType: string;
  qbSyncStatus?: "pending" | "synced" | "sync_failed";
  isActive: boolean;
  deletedAt?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ACCOUNT_TYPES = ["Expense", "Income", "Asset", "Liability", "Bank", "CreditCard"] as const;
type AccountType = (typeof ACCOUNT_TYPES)[number];

const D09_WARNING =
  "Editing this description will propagate to QuickBooks Desktop via AccountMod. Only edit if the GL meaning has truly changed; otherwise create a new GL.";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function GlAccountsAdminPage() {
  // --- MSAL identity (verbatim from AppShell.tsx pattern, lines 110-137) ---
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";

  // Admin role check — raw useQuery per AppShell pattern
  const roleEmailCandidates = [
    account?.username ?? "",
    claimStr("preferred_username"),
    claimStr("upn"),
    claimStr("email"),
  ].filter((e) => e && e.includes("@"));
  const userRecord = useQuery(
    api.users.findByAnyEmail,
    roleEmailCandidates.length > 0 ? { emails: roleEmailCandidates } : "skip",
  );
  const isAdmin = userRecord?.role === "admin";

  // --- Data queries (MANDATORY useStableQuery per D-33) ---
  const glAccountsRaw = useStableQuery(api.glAccounts.listActive, { includePaymentAccounts: true }, []);
  const usageCountsRaw = useStableQuery(api.glAccounts.usageCounts, {}, { vendorRefs: {}, transactionRefs: {} });

  const loading = glAccountsRaw === undefined;
  const glAccounts = glAccountsRaw ?? [];
  const usageCounts = usageCountsRaw ?? { vendorRefs: {}, transactionRefs: {} };

  // --- Mutations ---
  const glCreate = useMutation(api.glAccounts.create);
  const glUpdate = useMutation(api.glAccounts.update);
  const glSoftDelete = useMutation(api.glAccounts.softDelete);
  const glRetrySync = useMutation(api.glAccounts.retrySync);
  const glReconcile = useMutation(api.glAccounts.reconcileGlAccounts);

  // --- Filter state ---
  const [searchQ, setSearchQ] = useState("");
  const [accountTypeFilter, setAccountTypeFilter] = useState<"all" | AccountType>("all");

  // --- Snackbar state ---
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState<"success" | "error">("success");

  const showSnack = useCallback((message: string, severity: "success" | "error") => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  }, []);

  // --- Add/Edit dialog state ---
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<GlAccountRow | null>(null);
  const [errorAlert, setErrorAlert] = useState<string | undefined>(undefined);

  const openAddDialog = useCallback(() => {
    setEditingRow(null);
    setErrorAlert(undefined);
    setDialogOpen(true);
  }, []);

  const openEditDialog = useCallback((row: GlAccountRow) => {
    setEditingRow(row);
    setErrorAlert(undefined);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingRow(null);
    setErrorAlert(undefined);
  }, []);

  // --- Soft-delete confirm dialog state ---
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GlAccountRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openDeleteConfirm = useCallback((row: GlAccountRow) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  }, []);

  // --- Block error dialog state (D-10) ---
  const [blockErrorOpen, setBlockErrorOpen] = useState(false);
  const [blockErrorMsg, setBlockErrorMsg] = useState("");

  // --- Retry confirm dialog state (D-16) ---
  const [retryConfirmOpen, setRetryConfirmOpen] = useState(false);
  const [retryTarget, setRetryTarget] = useState<GlAccountRow | null>(null);

  const openRetryConfirm = useCallback((row: GlAccountRow) => {
    setRetryTarget(row);
    setRetryConfirmOpen(true);
  }, []);

  // --- Reconcile dialog state ---
  interface ReconcileDryRunReport {
    dryRun: true;
    total: number;
    alreadySynced: number;
    toCreate: number;
    items: { number: string; description: string }[];
  }
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [reconcileDryRunReport, setReconcileDryRunReport] = useState<ReconcileDryRunReport | null>(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileRunning, setReconcileRunning] = useState(false);

  const handleOpenReconcile = useCallback(async () => {
    setReconcileLoading(true);
    setReconcileDryRunReport(null);
    setReconcileDialogOpen(true);
    try {
      const report = await glReconcile({ dryRun: true, reviewerEmail: userRecord?.email ?? email });
      if (report.dryRun) {
        setReconcileDryRunReport(report as ReconcileDryRunReport);
      }
    } catch (err: any) {
      showSnack(err?.message ?? "Failed to load reconcile preview.", "error");
      setReconcileDialogOpen(false);
    } finally {
      setReconcileLoading(false);
    }
  }, [glReconcile, email, userRecord, showSnack]);

  const handleRunReconcile = useCallback(async () => {
    setReconcileRunning(true);
    try {
      const result = await glReconcile({ dryRun: false, reviewerEmail: userRecord?.email ?? email });
      if (!result.dryRun) {
        showSnack(
          `Enqueued ${result.enqueued} GL account(s) for QuickBooks. QBWC will process them on its next poll (~15 min).`,
          "success",
        );
      }
      setReconcileDialogOpen(false);
      setReconcileDryRunReport(null);
    } catch (err: any) {
      showSnack(err?.message ?? "Reconcile failed. Please try again.", "error");
    } finally {
      setReconcileRunning(false);
    }
  }, [glReconcile, email, userRecord, showSnack]);

  // --- Client-side filter ---
  const filteredRows = useMemo(() => {
    let rows = glAccounts;
    const q = searchQ.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r: GlAccountRow) =>
          r.number.includes(q) || r.description.toLowerCase().includes(q),
      );
    }
    if (accountTypeFilter !== "all") {
      rows = rows.filter((r: GlAccountRow) => r.accountType === accountTypeFilter);
    }
    return rows;
  }, [glAccounts, searchQ, accountTypeFilter]);

  // --- Submit handler (Add/Edit) ---
  const handleSubmit = useCallback(
    async (data: GlAccountFormData) => {
      setErrorAlert(undefined);
      if (!editingRow) {
        // Add mode
        try {
          await glCreate({
            number: data.number,
            description: data.description,
            accountType: data.accountType,
            reviewerEmail: userRecord?.email ?? email,
          });
          showSnack("GL Account added", "success");
          closeDialog();
        } catch (err: any) {
          const msg = err?.message ?? "Failed to save GL account. Please try again.";
          setErrorAlert(msg);
          throw err; // Re-throw so EntityFormDialog stays open
        }
      } else {
        // Edit mode — description only (D-18)
        try {
          await glUpdate({
            id: editingRow._id,
            description: data.description,
            reviewerEmail: userRecord?.email ?? email,
          });
          showSnack("GL Account updated", "success");
          closeDialog();
        } catch (err: any) {
          const msg = err?.message ?? "Failed to save GL account. Please try again.";
          setErrorAlert(msg);
          throw err;
        }
      }
    },
    [editingRow, email, glCreate, glUpdate, showSnack, closeDialog],
  );

  // --- Soft-delete confirm handler (D-10) ---
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await glSoftDelete({ id: deleteTarget._id, reviewerEmail: userRecord?.email ?? email });
      showSnack("GL Account deleted", "success");
      setDeleteOpen(false);
      setDeleteTarget(null);
    } catch (err: any) {
      // Convex throws verbatim D-10 message — surface in Dialog, NOT Snackbar
      setBlockErrorMsg(err.message ?? "Delete failed.");
      setBlockErrorOpen(true);
      setDeleteOpen(false);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, email, glSoftDelete, showSnack]);

  // --- Retry handler (D-16) ---
  const handleRetry = useCallback(async () => {
    if (!retryTarget) return;
    try {
      // Use dedicated retrySync mutation (WR-03): detects AccountAdd vs AccountMod
      // from qbListId presence without patching description or creating misleading audit entries
      await glRetrySync({
        id: retryTarget._id,
        reviewerEmail: userRecord?.email ?? email,
      });
      showSnack(`Retry queued for GL ${retryTarget.number}`, "success");
      setRetryConfirmOpen(false);
      setRetryTarget(null);
    } catch (err: any) {
      showSnack(err?.message ?? "Failed to queue retry.", "error");
      setRetryConfirmOpen(false);
      setRetryTarget(null);
    }
  }, [retryTarget, email, glRetrySync, showSnack]);

  // --- DataGrid columns ---
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "number",
        headerName: "GL Number",
        width: 110,
        renderCell: (p: GridRenderCellParams) => (
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", fontWeight: 700 }}
          >
            {p.value as string}
          </Typography>
        ),
      },
      {
        field: "description",
        headerName: "Description",
        flex: 2,
        minWidth: 200,
        renderCell: (p: GridRenderCellParams) => (
          <Typography
            variant="body2"
            title={p.value as string}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {p.value as string}
          </Typography>
        ),
      },
      {
        field: "accountType",
        headerName: "Account Type",
        width: 110,
        renderCell: (p: GridRenderCellParams) => (
          <Chip size="small" label={p.value as string} variant="outlined" />
        ),
      },
      {
        field: "qbSyncStatus",
        headerName: "QBD Sync",
        width: 140,
        renderCell: (p: GridRenderCellParams) => {
          const status = p.value as "pending" | "synced" | "sync_failed" | undefined;
          if (status === "pending")
            return <Chip size="small" label="Syncing" color="warning" />;
          if (status === "synced")
            return <Chip size="small" label="Synced" color="success" />;
          if (status === "sync_failed")
            return (
              <Chip
                size="small"
                label="Sync failed"
                color="error"
                clickable
                onClick={() => openRetryConfirm(p.row)}
                aria-label={`Retry sync for GL ${p.row.number}`}
                sx={{ cursor: "pointer" }}
              />
            );
          return <Chip size="small" label="Not synced" color="default" />;
        },
      },
      {
        field: "vendorRefCount",
        headerName: "Vendors using",
        width: 130,
        renderCell: (p: GridRenderCellParams) => {
          if (usageCountsRaw === undefined)
            return <Skeleton variant="text" width={40} />;
          const count = usageCounts.vendorRefs?.[p.row.number] ?? 0;
          return <Typography variant="body2">{count}</Typography>;
        },
      },
      {
        field: "txnRefCount",
        headerName: "Txn Refs",
        width: 100,
        renderCell: (p: GridRenderCellParams) => {
          if (usageCountsRaw === undefined)
            return <Skeleton variant="text" width={40} />;
          const count = usageCounts.transactionRefs?.[p.row.number] ?? 0;
          return <Typography variant="body2">{count}</Typography>;
        },
      },
      {
        field: "actions",
        headerName: "",
        width: 80,
        sortable: false,
        renderCell: (p: GridRenderCellParams) => (
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              aria-label={`Edit GL ${p.row.number}`}
              onClick={() => openEditDialog(p.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={`Delete GL ${p.row.number}`}
              onClick={() => openDeleteConfirm(p.row)}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [openEditDialog, openDeleteConfirm, openRetryConfirm, usageCounts],
  );

  // --- Filter bar ---
  const filterBar = (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      <TextField
        size="small"
        placeholder="Search by number or description"
        value={searchQ}
        onChange={(e) => setSearchQ(e.target.value)}
        sx={{ minWidth: 260 }}
      />
      <ToggleButtonGroup
        exclusive
        size="small"
        value={accountTypeFilter}
        onChange={(_e, val) => {
          if (val !== null) setAccountTypeFilter(val);
        }}
      >
        <ToggleButton value="all">All</ToggleButton>
        {ACCOUNT_TYPES.map((t) => (
          <ToggleButton key={t} value={t}>
            {t}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  );

  // --- Form default values ---
  const addDefaults: GlAccountFormData = useMemo(
    () => ({ number: "", description: "", accountType: "Expense" }),
    [],
  );
  const editDefaults: GlAccountFormData = useMemo(
    () => ({
      number: editingRow?.number ?? "",
      description: editingRow?.description ?? "",
      accountType: (editingRow?.accountType as AccountType) ?? "Expense",
    }),
    [editingRow],
  );

  // --- Edit schema (description-only — number/accountType are locked) ---
  // We use a partial approach: we pass the full glAccountSchema but render
  // number and accountType as read-only, so zod still validates them from
  // the default values that don't change.

  // --- Page-level role gate (CR-02) — mirrors VendorsAdminPage pattern ---
  if (userRecord === undefined) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don&apos;t have access to the GL Accounts admin page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* ------------------------------------------------------------------ */}
      {/* Main page                                                            */}
      {/* ------------------------------------------------------------------ */}
      {isAdmin && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleOpenReconcile}
            aria-label="Reconcile GL Accounts to QuickBooks"
          >
            Reconcile to QuickBooks
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={openAddDialog}
            aria-label="Add GL Account"
          >
            Add GL Account
          </Button>
        </Box>
      )}
      <DataTableShell
        data-tour="page-gl-accounts"
        title="GL Accounts"
        subtitle="Manage chart-of-accounts entries and QuickBooks sync"
        rows={filteredRows}
        columns={columns}
        loading={loading}
        totalBeforeFilters={glAccounts.length}
        filterBar={filterBar}
        onResetFilters={
          searchQ || accountTypeFilter !== "all"
            ? () => {
                setSearchQ("");
                setAccountTypeFilter("all");
              }
            : undefined
        }
        itemLabel="GL account"
        emptyTitle="No GL accounts found"
        emptyDescription="Add a GL account or wait for the Phase 6 seed to run."
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
          },
          disableRowSelectionOnClick: true,
          autoHeight: true,
        }}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit dialog (via EntityFormDialog)                             */}
      {/* ------------------------------------------------------------------ */}
      <EntityFormDialog<GlAccountFormData>
        title={editingRow ? "Edit GL Account" : "Add GL Account"}
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={handleSubmit}
        schema={glAccountSchema}
        defaultValues={editingRow ? editDefaults : addDefaults}
        submitLabel={editingRow ? "Save Changes" : "Add GL Account"}
        auditHref={
          editingRow ? `/audit?entity=glAccount&id=${editingRow._id}` : undefined
        }
        warningAlert={editingRow ? D09_WARNING : undefined}
        errorAlert={errorAlert}
        renderFields={(control, errors) => (
          <>
            {/* GL Number — read-only in edit mode */}
            <Controller
              name="number"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="GL Number"
                  size="small"
                  fullWidth
                  autoFocus={!editingRow}
                  helperText={
                    errors.number?.message ?? "4-digit number (e.g. 6010)"
                  }
                  error={!!errors.number}
                  InputProps={
                    editingRow
                      ? { readOnly: true }
                      : undefined
                  }
                  sx={editingRow ? { bgcolor: "var(--c-overlay-4)" } : undefined}
                />
              )}
            />

            {/* Description — always editable */}
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  size="small"
                  fullWidth
                  autoFocus={!!editingRow}
                  helperText={errors.description?.message}
                  error={!!errors.description}
                />
              )}
            />

            {/* Account Type — read-only in edit mode */}
            <Controller
              name="accountType"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth error={!!errors.accountType}>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    {...field}
                    label="Account Type"
                    inputProps={editingRow ? { readOnly: true } : undefined}
                    sx={editingRow ? { bgcolor: "var(--c-overlay-4)" } : undefined}
                  >
                    {ACCOUNT_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </>
        )}
      />

      {/* ------------------------------------------------------------------ */}
      {/* Soft-delete confirm dialog                                           */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={deleteOpen}
        maxWidth="xs"
        fullWidth
        aria-labelledby="confirm-delete-dialog-title"
      >
        <DialogTitle id="confirm-delete-dialog-title">Delete GL Account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Soft-delete GL {deleteTarget?.number} — {deleteTarget?.description}?
            It will no longer appear in vendor assignments or the Review queue.
            You can restore it from the audit trail.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleting}
            onClick={handleConfirmDelete}
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Blocked error dialog (D-10)                                         */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={blockErrorOpen}
        maxWidth="sm"
        fullWidth
        aria-labelledby="blocked-dialog-title"
      >
        <DialogTitle id="blocked-dialog-title">Cannot Delete GL Account</DialogTitle>
        <DialogContent>
          <Alert severity="error">{blockErrorMsg}</Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="contained" onClick={() => setBlockErrorOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Retry confirm dialog (D-16)                                         */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={retryConfirmOpen}
        maxWidth="xs"
        fullWidth
        aria-labelledby="retry-dialog-title"
      >
        <DialogTitle id="retry-dialog-title">Retry QuickBooks Sync?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Retry AccountAdd/AccountMod for GL {retryTarget?.number} —{" "}
            {retryTarget?.description}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setRetryConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRetry}>
            Retry
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Reconcile to QuickBooks dialog                                       */}
      {/* ------------------------------------------------------------------ */}
      <Dialog
        open={reconcileDialogOpen}
        maxWidth="sm"
        fullWidth
        aria-labelledby="reconcile-dialog-title"
        onClose={() => {
          if (!reconcileLoading && !reconcileRunning) {
            setReconcileDialogOpen(false);
            setReconcileDryRunReport(null);
          }
        }}
      >
        <DialogTitle id="reconcile-dialog-title">Reconcile GL Accounts to QuickBooks</DialogTitle>
        <DialogContent>
          {reconcileLoading && (
            <Stack alignItems="center" py={3} spacing={1}>
              <CircularProgress size={32} />
              <Typography variant="body2" color="text.secondary">
                Loading dry-run preview…
              </Typography>
            </Stack>
          )}
          {!reconcileLoading && reconcileDryRunReport && (
            <>
              <Stack direction="row" spacing={3} mb={2} flexWrap="wrap">
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total active GLs
                  </Typography>
                  <Typography variant="h6">{reconcileDryRunReport.total}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Already synced
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {reconcileDryRunReport.alreadySynced}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Will be created in QB
                  </Typography>
                  <Typography variant="h6" color="warning.main">
                    {reconcileDryRunReport.toCreate}
                  </Typography>
                </Box>
              </Stack>
              {reconcileDryRunReport.items.length > 0 && (
                <>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Accounts to create ({reconcileDryRunReport.items.length}):
                  </Typography>
                  <List dense disablePadding sx={{ maxHeight: 240, overflowY: "auto" }}>
                    {reconcileDryRunReport.items.map((item) => (
                      <ListItem key={item.number} disableGutters sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              <Typography
                                component="span"
                                variant="body2"
                                sx={{ fontFamily: "monospace", fontWeight: 700, mr: 1 }}
                              >
                                {item.number}
                              </Typography>
                              {item.description}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
              {reconcileDryRunReport.toCreate === 0 && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  All active GL accounts are already synced to QuickBooks.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => {
              setReconcileDialogOpen(false);
              setReconcileDryRunReport(null);
            }}
            disabled={reconcileLoading || reconcileRunning}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              reconcileLoading ||
              reconcileRunning ||
              !reconcileDryRunReport ||
              reconcileDryRunReport.toCreate === 0
            }
            onClick={handleRunReconcile}
            startIcon={
              reconcileRunning ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {reconcileRunning ? "Running…" : "Run reconcile"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          variant="filled"
          severity={snackSeverity}
          onClose={() => setSnackOpen(false)}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GlAccountsAdminPage;
